import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { findapiserverurl } from "./api.service";
import { getToken } from "./auth.header";

/* -------------------------------------------------------------------------------- *\
|                               LoginType
\* -------------------------------------------------------------------------------- */
export type LoginType =
{
    username: string;
    password: string;
}
/* -------------------------------------------------------------------------------- *\
|                               ProviderProps
\* -------------------------------------------------------------------------------- */
interface ProviderProps
{
    user: string | null,
    token: string,
    login(data: LoginType): Promise<any>,
    logout(): void,
}
/* -------------------------------------------------------------------------------- *\
|                               AuthContext
\* -------------------------------------------------------------------------------- */
const AuthContext = createContext<ProviderProps>({
    user: null,
    token: '',
    login: async (data: LoginType) => { Promise.resolve() },
    logout: () => { }
});
/* -------------------------------------------------------------------------------- *\
|                               AuthProvider
\* -------------------------------------------------------------------------------- */
export const AuthProvider = ({ children }: { children: React.ReactNode }) =>
{
    const storedInfo = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    const [user, setUser] = useState<string | null>(storedInfo?.username);
    const [token, setToken] = useState(storedInfo?.accessToken || '');
    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               login
    \* -------------------------------------------------------------------------------- */
    const login = async (data: LoginType) =>
    {
        const response = await axios.post(findapiserverurl() + "/api/signin", data);
        setUser(data.username);
        setToken(response.data.accessToken);
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
    }
    /* -------------------------------------------------------------------------------- *\
    |                               logout
    \* -------------------------------------------------------------------------------- */
    const logout = () =>
    {
        setUser(null);
        setToken('');
        localStorage.removeItem('user');
        navigate('/login');
    }
    /* -------------------------------------------------------------------------------- *\
    |                               checktoken
    \* -------------------------------------------------------------------------------- */
    const checktoken = useCallback(() =>
    {
        const t = getToken();
        if (t)
        {
            axios.post(findapiserverurl() + "/api/checktoken", { token: t }).then((response) =>
            {
                if (response.status !== 200) navigate('/login');
            })
            .catch(() => navigate('/login'));
        }
        else
        {
            navigate('/login');
        }
    // eslint-disable-next-line
    }, []);

    const location = useLocation();
    useEffect(() =>
    {
        const interval = setInterval(() =>
        {
            if (location.pathname !== "/login") checktoken();
        }, 10000);
        return () => clearInterval(interval);
    }, [checktoken, location.pathname]);

    return (
        <AuthContext.Provider value={{ user: user, token: token, login: login, logout: logout }}>
            {children}
        </AuthContext.Provider>
    );
}
/* -------------------------------------------------------------------------------- *\
|                               useAuthContext
\* -------------------------------------------------------------------------------- */
export const useAuthContext = () =>
{
    return useContext(AuthContext);
}
